import React from 'react';

import { Icon, Radio } from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { StyleSheet } from 'react-native';

import { Box } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';

import { AliasLabel, aliasIcons, aliasIconsSize, getAliasLabel } from '../types';

import {
  AddressInfoLine,
  AliasTextInfoLine,
  DeliveryAddressesButton,
  DeliveryAddressesButtonText,
  InfoLine,
} from './styled';
import { IDeliveryAddressItemProps } from './types';

export const DeliveryAddressItem: React.FC<React.PropsWithChildren<IDeliveryAddressItemProps>> = ({
  value,
  address,
  onEdit,
  onRemove,
}) => {
  const isDesktop = useIsDesktopBreakpoint();
  const aliasLabels = getAliasLabel();
  const baseAlias =
    (Object.keys(aliasIcons).find(base =>
      address.alias?.toLowerCase().startsWith(base.toLowerCase())
    ) as keyof typeof aliasIcons) || AliasLabel.Other;

  const iconVariant = aliasIcons[baseAlias];
  const iconSize = aliasIconsSize[baseAlias];
  const displayAlias = aliasLabels[baseAlias];

  const handleEditItem = () => {
    if (onEdit) {
      onEdit();
    }
  };

  const onRemoveItemPress = () => {
    onRemove?.();
  };

  return (
    <Box
      testID="delivery-address-item"
      style={[
        styles.addressContainer,
        isDesktop && styles.addressContainerDesktop,
        { borderTopWidth: value === '0' ? 1 : 0 },
      ]}
    >
      <Box style={styles.deliveryAddressesInfoContainer} testID="delivery-address-item-info">
        <Radio
          value={value}
          alignItems="center"
          justifyContent="space-between"
          flexDirection="row-reverse"
          width={'full'}
        >
          <Box style={styles.infoContainer}>
            <DatadogSessionReplayMask>
              <AddressInfoLine>{address.addressLine1}</AddressInfoLine>
            </DatadogSessionReplayMask>
            <DatadogSessionReplayMask>
              <InfoLine>
                {address.addressLine2 && (
                  <>{t('unitNumber', { unitNumber: address.addressLine2 })}, </>
                )}
                {address.city}, {address.state} {address.zip}, {address.phoneNumber}
              </InfoLine>
            </DatadogSessionReplayMask>
            {address.alias && (
              <InfoLine marginTop={2}>
                <Box style={styles.aliasInfoLine}>
                  <Icon variant={iconVariant} size={iconSize} color={primitive.$white} />
                  <AliasTextInfoLine>{displayAlias}</AliasTextInfoLine>
                </Box>
              </InfoLine>
            )}
          </Box>
        </Radio>
      </Box>
      <Box style={styles.deliveryAddressesEditContainer} testID="delivery-address-item-actions">
        <Box
          style={styles.deliveryAddressesEditButtonContainer}
          testID="delivery-address-item-action-buttons"
        >
          <DeliveryAddressesButton
            keepIconSize
            accessibilityLabel={`${t('remove')} ${address.addressLine1} `}
            testID="remove-button"
            onPress={onRemoveItemPress}
            leftIcon={<Icon variant="bkRemove" size="md" />}
            marginRight="$4"
          />
          <DeliveryAddressesButton
            accessibilityLabel={`${t('edit')} ${address.addressLine1} `}
            testID="edit-button"
            onPress={handleEditItem}
          >
            <DeliveryAddressesButtonText>{t('edit')}</DeliveryAddressesButtonText>
          </DeliveryAddressesButton>
        </Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  addressContainer: {
    alignSelf: 'center',
    backgroundColor: getDesignToken('background-pattern'),
    borderBottomWidth: 1,
    borderColor: primitive.$mayoEggWhite,
    borderRadius: 10,
    borderStyle: 'solid',
    flex: 1,
    justifyContent: 'center',
    marginTop: spacing.$3,
    paddingHorizontal: spacing.$0,
    paddingVertical: spacing.$2,
    width: '100%',
  },
  addressContainerDesktop: {
    width: '60%',
  },
  aliasInfoLine: {
    alignItems: 'center',
    backgroundColor: primitive.$bbqBrown,
    borderRadius: 3,
    flexDirection: 'row',
    gap: spacing.$1,
    justifyContent: 'center',
    paddingHorizontal: spacing.$1_5,
  },
  deliveryAddressesEditButtonContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    marginRight: spacing.$4,
    paddingHorizontal: spacing.$4,
  },
  deliveryAddressesEditContainer: {
    alignItems: 'center',
    borderColor: primitive.$bbqBrownOpacity20,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.$4,
  },
  deliveryAddressesInfoContainer: {
    flex: 1,
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$0,
  },
  infoContainer: {
    marginLeft: spacing.$4,
  },
});
